import React, { useRef, useEffect, useCallback } from 'react';

/**
 * AutoScrollCarousel - A smooth, continuously scrolling carousel
 *
 * Features:
 * - Auto-scrolls continuously from right to left
 * - Manual scroll: grab and drag to scroll manually
 * - Momentum: swipe and release to glide with inertia
 * - Resumes auto-scroll after inactivity
 * - Pauses on hover (desktop) for clicking
 *
 * Props:
 * - items: Array of items to display
 * - renderCard: Function to render each card (item, index) => JSX
 * - autoScroll: Whether to auto-scroll. Default: true
 * - scrollSpeed: Seconds per card (higher = slower). Default: 4
 * - scaleMax: Maximum scale at center (1.1 = 10% bigger). Default: 1.0 (disabled)
 * - cardWidth: Width of each card in pixels. Default: 280
 * - cardGap: Gap between cards in pixels. Default: 16
 * - resumeDelay: Seconds to wait before resuming auto-scroll after manual interaction. Default: 2
 * - momentum: Enable momentum/inertia scrolling. Default: true
 * - friction: Momentum friction (0.90 = quick stop, 0.98 = long glide). Default: 0.92
 */
function AutoScrollCarousel({
  items,
  renderCard,
  autoScroll = true,
  scrollSpeed = 4,
  scaleMax = 1.0,
  cardWidth = 280,
  cardGap = 16,
  resumeDelay = 2,
  momentum = true,
  friction = 0.92,
}) {
  const containerRef = useRef(null);
  const trackRef = useRef(null);
  const animationRef = useRef(null);
  const positionRef = useRef(0);
  const isPausedRef = useRef(false);
  const lastTimeRef = useRef(null);

  // Manual scroll state
  const isDraggingRef = useRef(false);
  const dragStartXRef = useRef(0);
  const dragStartPositionRef = useRef(0);
  const resumeTimeoutRef = useRef(null);

  // Momentum state
  const velocityRef = useRef(0);
  const isMomentumActiveRef = useRef(false);
  const lastDragXRef = useRef(0);
  const lastDragTimeRef = useRef(0);
  // Track recent positions for accurate velocity calculation
  const recentPositionsRef = useRef([]);

  // Calculate the total width of one set of items
  const singleSetWidth = items.length * (cardWidth + cardGap);

  // Pixels per second based on scroll speed (seconds per card)
  const pixelsPerSecond = (cardWidth + cardGap) / scrollSpeed;

  // Minimum velocity threshold to stop momentum (pixels per second)
  const velocityThreshold = 10;

  // Wrap position for seamless looping
  const wrapPosition = useCallback((pos) => {
    if (pos > 0) {
      return pos - singleSetWidth;
    } else if (Math.abs(pos) >= singleSetWidth) {
      return pos % singleSetWidth;
    }
    return pos;
  }, [singleSetWidth]);

  // Update card scales based on distance from center (direct DOM manipulation)
  const updateCardScales = useCallback(() => {
    if (!containerRef.current || !trackRef.current || scaleMax <= 1.0) return;

    const container = containerRef.current;
    const containerRect = container.getBoundingClientRect();
    const containerCenter = containerRect.width / 2;

    const cards = trackRef.current.querySelectorAll('.carousel-card');

    cards.forEach((card) => {
      const cardRect = card.getBoundingClientRect();
      // Card center relative to container
      const cardCenterInContainer = cardRect.left - containerRect.left + cardRect.width / 2;

      // Distance from center (0 = center, 1 = edge)
      const distance = Math.abs(containerCenter - cardCenterInContainer);
      const maxDistance = containerRect.width / 2 + cardWidth / 2;
      const normalizedDistance = Math.min(distance / maxDistance, 1);

      // Scale: scaleMax at center, 1.0 at edges (using easeOutQuad for smoother transition)
      const eased = 1 - Math.pow(normalizedDistance, 2);
      const scale = 1 + (scaleMax - 1) * eased;

      // Apply transform directly to DOM
      card.style.transform = `scale(${scale})`;
    });
  }, [scaleMax, cardWidth]);

  // Clear the resume timeout
  const clearResumeTimeout = useCallback(() => {
    if (resumeTimeoutRef.current) {
      clearTimeout(resumeTimeoutRef.current);
      resumeTimeoutRef.current = null;
    }
  }, []);

  // Schedule resuming auto-scroll
  const scheduleResume = useCallback(() => {
    clearResumeTimeout();
    resumeTimeoutRef.current = setTimeout(() => {
      isPausedRef.current = false;
      lastTimeRef.current = null; // Reset time to avoid jump
    }, resumeDelay * 1000);
  }, [resumeDelay, clearResumeTimeout]);

  // Animation loop
  const animate = useCallback((currentTime) => {
    if (!lastTimeRef.current) {
      lastTimeRef.current = currentTime;
    }

    const deltaTime = (currentTime - lastTimeRef.current) / 1000;
    lastTimeRef.current = currentTime;

    // Handle momentum animation
    if (isMomentumActiveRef.current && trackRef.current) {
      // Apply velocity to position
      positionRef.current += velocityRef.current * deltaTime;

      // Apply friction to slow down
      velocityRef.current *= friction;

      // Wrap position for seamless loop
      positionRef.current = wrapPosition(positionRef.current);

      // Apply transform
      trackRef.current.style.transform = `translateX(${positionRef.current}px)`;
      updateCardScales();

      // Stop momentum when velocity is low enough
      if (Math.abs(velocityRef.current) < velocityThreshold) {
        isMomentumActiveRef.current = false;
        velocityRef.current = 0;
        // Schedule auto-scroll resume after momentum stops
        scheduleResume();
      }
    }
    // Handle auto-scroll animation
    else if (!isPausedRef.current && !isDraggingRef.current && !isMomentumActiveRef.current && trackRef.current) {
      // Move position (negative = scroll left)
      positionRef.current -= pixelsPerSecond * deltaTime;

      // Reset position when we've scrolled one full set (seamless loop)
      positionRef.current = wrapPosition(positionRef.current);

      // Apply transform to track
      trackRef.current.style.transform = `translateX(${positionRef.current}px)`;

      // Update card spotlight scales
      updateCardScales();
    }

    animationRef.current = requestAnimationFrame(animate);
  }, [pixelsPerSecond, friction, wrapPosition, updateCardScales, scheduleResume]);

  // Start animation on mount (only if autoScroll is enabled)
  useEffect(() => {
    if (autoScroll) {
      animationRef.current = requestAnimationFrame(animate);
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      clearResumeTimeout();
    };
  }, [animate, autoScroll, clearResumeTimeout]);

  // Get X coordinate from mouse or touch event
  const getClientX = (e) => {
    if (e.touches && e.touches.length > 0) {
      return e.touches[0].clientX;
    }
    return e.clientX;
  };

  // Handle drag start (mouse or touch)
  const handleDragStart = (e) => {
    // Don't prevent default - allow clicks to work
    isDraggingRef.current = true;
    isPausedRef.current = true;
    isMomentumActiveRef.current = false;
    velocityRef.current = 0;
    clearResumeTimeout();

    const clientX = getClientX(e);
    dragStartXRef.current = clientX;
    dragStartPositionRef.current = positionRef.current;
    lastDragXRef.current = clientX;
    lastDragTimeRef.current = performance.now();

    // Clear recent positions for fresh velocity tracking
    recentPositionsRef.current = [{
      x: clientX,
      time: performance.now()
    }];

    // Add cursor style
    if (containerRef.current) {
      containerRef.current.style.cursor = 'grabbing';
    }
  };

  // Handle drag move
  const handleDragMove = (e) => {
    if (!isDraggingRef.current || !trackRef.current) return;

    const currentX = getClientX(e);
    const currentTime = performance.now();
    const deltaX = currentX - dragStartXRef.current;

    // Track recent positions for velocity calculation (keep last 5)
    recentPositionsRef.current.push({ x: currentX, time: currentTime });
    if (recentPositionsRef.current.length > 5) {
      recentPositionsRef.current.shift();
    }

    // Update position based on drag
    let newPosition = dragStartPositionRef.current + deltaX;

    // Wrap position for seamless looping
    newPosition = wrapPosition(newPosition);

    positionRef.current = newPosition;
    trackRef.current.style.transform = `translateX(${positionRef.current}px)`;

    // Update last drag position/time
    lastDragXRef.current = currentX;
    lastDragTimeRef.current = currentTime;

    // Update scales if spotlight is enabled
    updateCardScales();
  };

  // Handle drag end
  const handleDragEnd = () => {
    if (!isDraggingRef.current) return;

    isDraggingRef.current = false;

    // Reset cursor
    if (containerRef.current) {
      containerRef.current.style.cursor = 'grab';
    }

    // Calculate velocity from recent positions
    if (momentum && recentPositionsRef.current.length >= 2) {
      const positions = recentPositionsRef.current;
      const oldest = positions[0];
      const newest = positions[positions.length - 1];
      const timeDiff = (newest.time - oldest.time) / 1000; // Convert to seconds

      if (timeDiff > 0 && timeDiff < 0.3) { // Only use if recent enough (within 300ms)
        const distanceDiff = newest.x - oldest.x;
        const calculatedVelocity = distanceDiff / timeDiff; // pixels per second

        // Only apply momentum if velocity is significant
        if (Math.abs(calculatedVelocity) > 50) {
          velocityRef.current = calculatedVelocity;
          isMomentumActiveRef.current = true;
          // Don't schedule resume yet - wait for momentum to finish
          return;
        }
      }
    }

    // No momentum - schedule resume directly
    scheduleResume();
  };

  // Pause on hover (desktop only - for clicking cards)
  const handleMouseEnter = () => {
    if (!isDraggingRef.current && !isMomentumActiveRef.current) {
      isPausedRef.current = true;
      clearResumeTimeout();
    }
  };

  const handleMouseLeave = () => {
    if (!isDraggingRef.current && !isMomentumActiveRef.current) {
      scheduleResume();
    }
    // Also end drag if mouse leaves while dragging
    if (isDraggingRef.current) {
      handleDragEnd();
    }
  };

  if (!items || items.length === 0) return null;

  // Duplicate items for seamless loop (only when auto-scrolling)
  const displayItems = autoScroll ? [...items, ...items] : items;

  // If not auto-scrolling, render a simple scrollable carousel
  if (!autoScroll) {
    return (
      <div className="auto-scroll-carousel manual-scroll">
        <div className="carousel-track" style={{ gap: `${cardGap}px` }}>
          {displayItems.map((item, index) => (
            <div
              key={`card-${index}`}
              className="carousel-card"
              style={{ width: `${cardWidth}px`, flexShrink: 0 }}
            >
              {renderCard(item, index)}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="auto-scroll-carousel"
      style={{ cursor: 'grab' }}
      // Mouse events for desktop
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseDown={handleDragStart}
      onMouseMove={handleDragMove}
      onMouseUp={handleDragEnd}
      // Touch events for mobile
      onTouchStart={handleDragStart}
      onTouchMove={handleDragMove}
      onTouchEnd={handleDragEnd}
    >
      <div
        ref={trackRef}
        className="carousel-track"
        style={{ gap: `${cardGap}px` }}
      >
        {displayItems.map((item, index) => (
          <div
            key={`card-${index}`}
            className="carousel-card"
            style={{ width: `${cardWidth}px`, flexShrink: 0 }}
          >
            {renderCard(item, index % items.length)}
          </div>
        ))}
      </div>
    </div>
  );
}

export default AutoScrollCarousel;
